import { useCallback, useEffect, useRef, useState } from 'react';
import { assistantService } from '../services/assistant.service';
import type {
  ActionProposee,
  ContexteDossier,
  Message,
  MessageAssistant,
} from '../types/assistant.types';

let compteur = 0;

/**
 * La conversation : ce qui a été dit, ce qui attend une confirmation.
 * Une seule action peut être « à confirmer » à la fois — on ne demande pas
 * deux décisions d'un coup (règle 7 du standard d'écran).
 *
 * Une action confirmée peut durer plusieurs minutes (robot navigateur) :
 * on interroge son état toutes les trois secondes jusqu'à « faite » ou
 * « échec », et on arrête dès qu'on quitte l'écran.
 */
export function useAssistant(contexte: ContexteDossier | null) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [enAttente, setEnAttente] = useState(false);
  /** Lu de façon synchrone : deux clics dans le même rendu ne font qu'un envoi. */
  const envoiEnCours = useRef(false);
  const [conversationUuid, setConversationUuid] = useState<
    string | undefined
  >();
  const suivis = useRef<Map<string, number>>(new Map());
  /** Incrémenté à chaque « Nouvelle conversation » : une réponse en vol d'un fil effacé est ignorée. */
  const generation = useRef(0);

  useEffect(() => {
    const encours = suivis.current;
    return () => {
      encours.forEach(id => window.clearInterval(id));
      encours.clear();
    };
  }, []);

  const remplacerAction = useCallback(
    (idMessage: string, action: ActionProposee) => {
      setMessages(prev =>
        prev.map(m =>
          m.id === idMessage && m.auteur === 'assistant'
            ? ({ ...m, action } as MessageAssistant)
            : m
        )
      );
    },
    []
  );

  const suivre = useCallback(
    (idMessage: string, action: ActionProposee) => {
      if (suivis.current.has(action.uuid)) return;
      const debut = Date.now();
      const id = window.setInterval(async () => {
        // Au-delà de 25 minutes, on cesse d'interroger et on le dit : le
        // serveur, lui, a déclaré l'action en échec à 20 minutes.
        if (Date.now() - debut > 25 * 60_000) {
          window.clearInterval(id);
          suivis.current.delete(action.uuid);
          remplacerAction(idMessage, {
            ...action,
            etat: 'echec',
            resultat: {
              texte:
                'Toujours en cours après 25 minutes : vérifiez l’historique du dossier avant de relancer.',
            },
          });
          return;
        }
        try {
          const etat = await assistantService.lireAction(action.uuid);
          remplacerAction(idMessage, etat);
          if (etat.etat !== 'en_cours') {
            window.clearInterval(id);
            suivis.current.delete(action.uuid);
          }
        } catch {
          // Une lecture ratée n'arrête pas le suivi : la prochaine réessaie.
        }
      }, 3000);
      suivis.current.set(action.uuid, id);
    },
    [remplacerAction]
  );

  const envoyer = useCallback(
    async (texte: string) => {
      const propre = texte.trim();
      if (!propre || enAttente || envoiEnCours.current) return;
      envoiEnCours.current = true;
      setMessages(prev => [
        ...prev,
        { id: `u-${++compteur}`, auteur: 'utilisateur', texte: propre },
      ]);
      setEnAttente(true);
      const gen = generation.current;
      try {
        const { conversationUuid: conv, message } =
          await assistantService.envoyer(
            propre,
            conversationUuid,
            contexte?.uuid
          );
        // « Nouvelle conversation » a été cliqué pendant l'attente : cette
        // réponse appartient au fil effacé, on ne la recolle pas.
        if (gen !== generation.current) return;
        setConversationUuid(conv);
        setMessages(prev => [...prev, message]);
      } catch (e) {
        if (gen !== generation.current) return;
        const statut = (e as { response?: { status?: number } })?.response
          ?.status;
        setMessages(prev => [
          ...prev,
          {
            id: `a-${++compteur}`,
            auteur: 'assistant',
            limite: true,
            texte:
              statut === 503
                ? 'L’assistant n’est pas activé sur ce serveur.'
                : statut === 403
                  ? 'Cet espace n’est pas accessible depuis cet accès.'
                  : statut === 400
                    ? 'La demande est trop longue : 2 000 caractères au plus.'
                    : 'Je n’ai pas pu traiter la demande. Réessayez dans un instant.',
          },
        ]);
      } finally {
        envoiEnCours.current = false;
        if (gen === generation.current) setEnAttente(false);
      }
    },
    [contexte, conversationUuid, enAttente]
  );

  const confirmer = useCallback(
    async (idMessage: string, action: ActionProposee) => {
      remplacerAction(idMessage, { ...action, etat: 'en_cours' });
      try {
        const etat = await assistantService.confirmer(action.uuid);
        remplacerAction(idMessage, etat);
        if (etat.etat === 'en_cours') suivre(idMessage, etat);
      } catch {
        // La requête a échoué, mais le serveur a peut-être déjà pris l'action :
        // on relit l'état plutôt que d'affirmer ce qu'on ne sait pas.
        try {
          const etat = await assistantService.lireAction(action.uuid);
          remplacerAction(idMessage, etat);
          if (etat.etat === 'en_cours') suivre(idMessage, etat);
        } catch {
          remplacerAction(idMessage, {
            ...action,
            etat: 'echec',
            resultat: {
              texte:
                'Impossible de savoir si l’action est partie : vérifiez l’historique du dossier avant de relancer.',
            },
          });
        }
      }
    },
    [remplacerAction, suivre]
  );

  const annuler = useCallback(
    async (idMessage: string, action: ActionProposee) => {
      remplacerAction(idMessage, { ...action, etat: 'annulee' });
      try {
        // Le serveur a le dernier mot : une action déjà partie ne redevient
        // pas « annulée » à l'écran parce qu'on a cliqué trop tard.
        const etat = await assistantService.annuler(action.uuid);
        remplacerAction(idMessage, etat);
        if (etat.etat === 'en_cours') suivre(idMessage, etat);
      } catch {
        // L'écran a déjà annulé ; le serveur refusera de toute façon une
        // confirmation ultérieure sur une action expirée.
      }
    },
    [remplacerAction, suivre]
  );

  const recommencer = useCallback(() => {
    generation.current += 1;
    suivis.current.forEach(id => window.clearInterval(id));
    suivis.current.clear();
    setMessages([]);
    setConversationUuid(undefined);
    setEnAttente(false);
    envoiEnCours.current = false;
  }, []);

  /** Tant qu'une action attend une décision, on ne prend pas la suivante. */
  const actionEnSuspens = messages.some(
    m => m.auteur === 'assistant' && m.action?.etat === 'a_confirmer'
  );
  /** Une action confirmée tourne encore (robot, dépôt) : on la suit à l'écran. */
  const actionEnCours = messages.some(
    m => m.auteur === 'assistant' && m.action?.etat === 'en_cours'
  );
  /** Tant qu'une action attend ou tourne, on ne quitte pas ce dossier. */
  const occupe = actionEnSuspens || actionEnCours;

  return {
    messages,
    enAttente,
    actionEnSuspens,
    actionEnCours,
    occupe,
    envoyer,
    confirmer,
    annuler,
    recommencer,
  };
}
